import React, { FunctionComponent } from 'react';

import Cta from '@gojob/ui-bits/Cta';
import Flex from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Text from '@gojob/ui-bits/Text';
import { GatsbyImageProps } from 'gatsby-plugin-image';

import Story from '../ArticleCategory/Catalogue/Story';
import BreakpointContainer from '../BreakpointContainer';
import Link from '../Link';

export type FeaturedArticlesProps = {
  articles?: FeaturedArticlesResponse[];
  title?: string;
  buttonLabel?: string;
  category?: {
    title?: string;
    slug?: {
      current?: string;
    };
  };
};

const useStyles = () => {
  const { colors, spacing, breakpoint } = useTheme();

  return {
    root: {
      backgroundColor: colors.lightBackground,
      boxShadow: `inset 0 0 6px 0 rgba(35,44,122,0.26)`,
      width: '100%',
    },
    container: {
      width: 'auto',
      justifyContent: 'center',
    },
    item: {
      margin: spacing(2, 0),
      flex: '0 0 100%',
      [breakpoint.up('md')]: {
        margin: spacing(2),
        flex: `0 0 calc(50% - ${spacing(4)})`,
      },
      [breakpoint.up('lg')]: {
        flex: `0 0 calc(33% - ${spacing(4)})`,
      },
    },
    title: {
      margin: spacing(0, 0, 3),
    },
    button: {
      margin: spacing(8, 0, 4),
      width: '100%',
      maxWidth: spacing(75),
    },
  } as const;
};

const FeaturedArticles: FunctionComponent<FeaturedArticlesProps> = (props) => {
  const { articles, title, category, buttonLabel } = props;
  const classes = useStyles();

  if (!articles || !articles.length) {
    return null;
  }

  return (
    <Flex css={classes.root} flexDirection="column" alignItems="center" margin={[8, -4, -4]} padding={[5, 4]}>
      <Text variant="headline2" as="h2" css={classes.title}>
        {title}
      </Text>
      <BreakpointContainer
        css={classes.container}
        maxWidth="xl"
        flexDirection="row"
        flexWrap="wrap"
        alignItems="stretch"
      >
        {articles?.map(({ category, ...article }, index) => {
          return <Story key={index} css={classes.item} category={category?.title} {...article} />;
        })}
      </BreakpointContainer>

      {category?.slug?.current && buttonLabel && (
        <Flex margin={[0, 4]} width="100%" justifyContent="center">
          <Cta as={Link} variant="secondary" to={category?.slug?.current} css={classes.button}>
            {buttonLabel}
          </Cta>
        </Flex>
      )}
    </Flex>
  );
};

export type FeaturedArticlesResponse = {
  id?: string;
  slug?: {
    current?: string;
  };
  title?: string;
  subtitle?: string;
  category?: {
    title?: string;
  };
  image: {
    alt: string;
    hotspot?: {
      x?: number;
      y?: number;
    };
    asset: {
      localFile: {
        childImageSharp: {
          gatsbyImageData: GatsbyImageProps['image'];
        };
      };
    };
  };
};

export default FeaturedArticles;
